/**
 * REST client for the Live Ball backend. Mirrors the conventions of the
 * AMG single-player API used by Dragon Tiger:
 *
 *   - POST /action/login    { userName, agentId, password }       → SessionId
 *   - POST /action/opengame { ..., gameKey, md5 } (Bearer login.sessionId)
 *                           → CurrentGameUrl with ?token=&gsid=
 *   - POST /action/balance  (priming step) → balance + refreshed token
 *   - POST /action/getinfo  → { Balance, GameKey, … }
 *   - POST /action/bet      { betDetail: BetLine[], gid, gameKey, md5 }
 *
 * Both envelopes accepted:
 *   Legacy: { ErrorCode, Message, Data }
 *   New:    { success, error, message, data }
 */
import { Endpoints } from '../config';
import { appendDebugLog } from '../ui/debugPanel';
import { generateBalanceMd5, generateInfoMd5 } from './md5';
import type {
  BetLine,
  BetResult,
  BetResultItem,
  GameSession,
  SessionCredentials,
  UserInfo,
} from '../types/api';
import { debugLog, error as logError } from '../util/logger';

export class ApiError extends Error {
  status: number;
  unauthorized: boolean;
  constructor(message: string, status: number, unauthorized = false) {
    super(message);
    this.status = status;
    this.unauthorized = unauthorized;
  }
}

export class Api {
  private creds: SessionCredentials = {
    sessionToken: null,
    gameSessionId: null,
    agentId: '',
    userName: '',
  };

  setCredentials(c: SessionCredentials): void {
    this.creds = { ...c };
  }
  getCredentials(): Readonly<SessionCredentials> {
    return this.creds;
  }

  private get headers(): Record<string, string> {
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    if (this.creds.sessionToken) h.Authorization = `Bearer ${this.creds.sessionToken}`;
    return h;
  }

  private async post(url: string, body: Record<string, unknown>): Promise<unknown> {
    debugLog('[API][REQUEST] POST', url, body);
    let resp: Response;
    try {
      resp = await fetch(url, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify(body),
      });
    } catch (e) {
      logError('[API][NETWORK]', e);
      throw new ApiError(String(e), 0);
    }
    const text = await resp.text();
    debugLog('[API][RESPONSE]', url, resp.status, text);
    if (resp.status === 401) throw new ApiError('Unauthorized', 401, true);
    if (resp.status !== 200) throw new ApiError(text || `HTTP ${resp.status}`, resp.status);
    try {
      return JSON.parse(text);
    } catch (e) {
      throw new ApiError(`Invalid JSON: ${String(e)}`, resp.status);
    }
  }

  private unwrap(json: unknown): Record<string, unknown> {
    if (!json || typeof json !== 'object') {
      throw new ApiError('Invalid response body: expected object', 200);
    }
    const obj = json as Record<string, unknown>;
    if ('ErrorCode' in obj) {
      const code = Number(obj.ErrorCode ?? 0);
      if (code !== 0) {
        throw new ApiError((obj.Message as string) ?? `ErrorCode ${code}`, 200);
      }
      return this.extractData(obj);
    }
    if (obj.success === false || obj.error != null) {
      throw new ApiError(
        (obj.message as string) ?? (obj.error as string) ?? 'Unknown error',
        200,
      );
    }
    return this.extractData(obj);
  }

  private extractData(json: Record<string, unknown>): Record<string, unknown> {
    const field = json.data ?? json.Data;
    if (field && typeof field === 'object' && !Array.isArray(field)) {
      return field as Record<string, unknown>;
    }
    if (Array.isArray(field) && field.length > 0 && typeof field[0] === 'object') {
      return field[0] as Record<string, unknown>;
    }
    return json;
  }

  // ============== Endpoints ==============

  async login(opts: { userName: string; agentId: string; password: string }) {
    const body: Record<string, unknown> = {
      userName: opts.userName,
      agentId: opts.agentId,
      password: opts.password,
    };
    const raw = await this.post(Endpoints.login, body);
    const data = this.unwrap(raw);
    return {
      sessionId: str(pick(data, 'SessionId', 'sessionId')),
      balance: num(pick(data, 'Balance', 'balance')),
      agentId: str(pick(data, 'AgentId', 'agentId')),
      userName: str(pick(data, 'UserName', 'userName')),
    };
  }

  async openGame(opts: { userName: string; agentId: string; gameKey: string }): Promise<GameSession> {
    const md5 = generateBalanceMd5(opts.agentId, opts.gameKey, opts.userName);
    const body: Record<string, unknown> = {
      userName: opts.userName,
      agentId: opts.agentId,
      token: 'token',
      gameKey: opts.gameKey,
      md5,
    };
    const raw = await this.post(Endpoints.openGame, body);
    const data = this.unwrap(raw);
    return parseGameSession(data);
  }

  async checkBalance(opts: { sessionId: string; gameKey: string }): Promise<number> {
    if (!this.creds.gameSessionId) this.creds.gameSessionId = opts.sessionId;
    const md5 = generateBalanceMd5(this.creds.agentId, opts.gameKey, this.creds.userName);
    const body: Record<string, unknown> = {
      userName: this.creds.userName,
      agentId: this.creds.agentId,
      gameKey: opts.gameKey,
      md5,
    };
    if (this.creds.sessionToken) body.token = this.creds.sessionToken;
    const raw = await this.post(Endpoints.checkBalance, body);
    const data = this.unwrap(raw);
    const nextToken = (data.sessionId ?? data.SessionId) as string | undefined;
    if (nextToken) this.creds.sessionToken = nextToken;
    const nextAgent = (data.agentId ?? data.AgentId) as string | undefined;
    if (nextAgent) this.creds.agentId = nextAgent;
    return Number((data.balance ?? data.Balance) ?? 0);
  }

  async getInfo(opts: { sessionId: string; gameKey: string }): Promise<UserInfo> {
    if (!this.creds.gameSessionId) this.creds.gameSessionId = opts.sessionId;
    const md5 = generateInfoMd5(this.creds.userName);
    const body: Record<string, unknown> = {
      userName: this.creds.userName,
      gameKey: opts.gameKey,
      md5,
    };
    if (this.creds.gameSessionId) body.gameSessionId = this.creds.gameSessionId;
    const raw = await this.post(Endpoints.getInfo, body);
    const data = this.unwrap(raw);
    return parseUserInfo(data);
  }

  async placeBet(opts: {
    sessionId: string;
    bets: BetLine[];
    gid: string;
    gameKey: string;
  }): Promise<BetResult> {
    const md5 = generateBalanceMd5(this.creds.agentId, opts.gameKey, this.creds.userName);
    const body: Record<string, unknown> = {
      userName: this.creds.userName,
      agentId: this.creds.agentId,
      token: 'string',
      gameKey: opts.gameKey,
      md5,
      betDetail: opts.bets,
      gid: opts.gid,
    };
    const raw = await this.post(Endpoints.placeBet, body);
    // TEMP debug — surface the raw /action/bet response in the on-screen log.
    // Remove together with the rest of the debug panel wiring.
    appendDebugLog('SP /action/bet response', raw);
    const data = this.unwrap(raw);
    return parseBetResult(data);
  }
}

// ==================== Parsers ====================

function num(v: unknown, dflt = 0): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && v !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : dflt;
  }
  return dflt;
}
function str(v: unknown, dflt = ''): string {
  return typeof v === 'string' ? v : dflt;
}
function bool(v: unknown, dflt = false): boolean {
  return typeof v === 'boolean' ? v : dflt;
}
function pick<T = unknown>(o: Record<string, unknown>, ...keys: string[]): T | undefined {
  for (const k of keys) {
    if (o[k] !== undefined && o[k] !== null) return o[k] as T;
  }
  return undefined;
}

function parseGameSession(d: Record<string, unknown>): GameSession {
  return {
    balance: num(pick(d, 'Balance', 'balance')),
    currentGameUrl: str(pick(d, 'CurrentGameUrl', 'currentGameUrl')),
    agentId: str(pick(d, 'AgentId', 'agentId')),
    sessionId: str(pick(d, 'SessionId', 'sessionId')),
    gameName: str(pick(d, 'GameName', 'gameName')),
    nextRoundId: str(pick(d, 'NextRoundId', 'nextRoundId')),
    chips: parseChipList(str(pick(d, 'Chips', 'chips'))),
    minBet: num(pick(d, 'MinBet', 'minBet')),
    maxBet: num(pick(d, 'MaxBet', 'maxBet')),
  };
}

function parseUserInfo(d: Record<string, unknown>): UserInfo {
  return {
    balance: num(pick(d, 'Balance', 'balance')),
    agentId: str(pick(d, 'AgentId', 'agentId')),
    userName: str(pick(d, 'UserName', 'userName')),
    sessionId: str(pick(d, 'SessionId', 'sessionId')),
    gameKey: str(pick(d, 'GameKey', 'gameKey')),
    gameName: str(pick(d, 'GameName', 'gameName')),
    isActive: bool(pick(d, 'IsActive', 'isActive'), true),
    isLock: bool(pick(d, 'IsLock', 'isLock')),
    avatarId: num(pick(d, 'AvatarId', 'avatarId')),
    chips: parseChipList(str(pick(d, 'Chips', 'chips'))),
    minBet: num(pick(d, 'MinBet', 'minBet')),
    maxBet: num(pick(d, 'MaxBet', 'maxBet')),
  };
}

/** Server sends chip denominations as a comma-separated string. */
function parseChipList(s: string): number[] {
  if (!s) return [];
  return s
    .split(',')
    .map((p) => Number(p.trim()))
    .filter((n) => Number.isFinite(n) && n > 0);
}

function parseBetResultItem(d: Record<string, unknown>): BetResultItem {
  const winLose = num(pick(d, 'winLose', 'WinLose'));
  const rawIsWin = pick<unknown>(d, 'isWin', 'IsWin');
  return {
    betItemId: num(pick(d, 'betItemId', 'BetItemId', 'BetIemId')),
    betAmount: num(pick(d, 'betAmount', 'Amount', 'amount')),
    winLose,
    isWin: typeof rawIsWin === 'boolean' ? rawIsWin : winLose > 0,
  };
}

function parseBetResult(d: Record<string, unknown>): BetResult {
  const arrRaw = pick<unknown[]>(d, 'BetResults', 'betResults', 'betResult') ?? [];
  const betResults = (arrRaw as unknown[])
    .filter((x): x is Record<string, unknown> => !!x && typeof x === 'object')
    .map(parseBetResultItem);

  const totalWinLose = num(pick(d, 'TotalWinLose', 'totalWinLose'));
  const resultStr = str(pick(d, 'Result', 'result'));
  const { ballNumber, winningBetIds } = parseResultString(resultStr, d);

  let positive = 0;
  let hasPositive = false;
  for (const r of betResults) {
    if (r.winLose > 0) {
      positive += r.winLose;
      hasPositive = true;
    }
  }

  return {
    result: resultStr,
    ballNumber,
    winningBetIds,
    totalWinLose,
    afterBalance: num(pick(d, 'Balance', 'afterBalance', 'balance')),
    nextRoundId: str(pick(d, 'NextRoundId', 'nextRoundId')),
    betResults,
    positiveWinAmount: positive,
    hasPositiveWin: hasPositive,
  };
}

/**
 * Live Ball result format: `"<ballNumber>,@<winId>,@<winId>,…"` — e.g.
 *   "2,@391,@397,@399"  → ball=2, winning bet IDs [391, 397, 399].
 *
 * Falls back to numeric fields (`ballNumber`, `WinningNumber`) and to a
 * loose `\d+` regex if the live format isn't matched.
 */
function parseResultString(s: string, d: Record<string, unknown>): {
  ballNumber: number;
  winningBetIds: number[];
} {
  const winningBetIds: number[] = [];
  let ballNumber = num(pick(d, 'ballNumber', 'BallNumber', 'WinningNumber', 'winningNumber'));

  if (s) {
    const parts = s.split(',').map((p) => p.trim()).filter(Boolean);
    if (parts.length > 0 && ballNumber <= 0) {
      const n = Number(parts[0]);
      if (Number.isFinite(n)) ballNumber = n;
    }
    for (const p of parts) {
      if (p.startsWith('@')) {
        const id = Number(p.slice(1));
        if (Number.isFinite(id)) winningBetIds.push(id);
      }
    }
    if (ballNumber <= 0) {
      const m = s.match(/\d+/);
      if (m) ballNumber = Number(m[0]);
    }
  }
  return { ballNumber, winningBetIds };
}
